import { useState } from "react";
import { Link } from "wouter";
import { trpc } from "../../_core/trpc";
import { Panel } from "./Admin";
import { formatInZone, zoneLabel } from "@/shared/birthday";
function AdminOverview() {
  const overview = trpc.admin.overview.useQuery();
  const publish = trpc.admin.publish.useMutation();
  const utils = trpc.useUtils();
  const [note, setNote] = useState(null);
  const d = overview.data;
  const doPublish = async () => {
    setNote(null);
    try {
      const res = await publish.mutateAsync({});
      setNote(
        `Published ${new Date(res.publishedAt).toLocaleString()}. ${res.unlocked ? "Her site is unlocked and live." : "Her site is showing the countdown."}`
      );
      await utils.admin.overview.invalidate();
    } catch (e) {
      setNote(e instanceof Error ? e.message : "Publish failed.");
    }
  };
  if (overview.isLoading) return <Panel eyebrow="Overview" title="Loading…"><div /></Panel>;
  if (overview.error || !d) {
    return <Panel eyebrow="Overview" title="Couldn't load the studio">
        <div className="glass rounded-xl p-6 text-sm text-[#f3c2ce]">
          {overview.error?.message ?? "Unknown error."} Try refreshing the page.
        </div>
      </Panel>;
  }
  const birthday = new Date(d.birthdayAt);
  return <Panel
    eyebrow="Overview"
    title={d.unlocked ? "Her world is open" : "Waiting for midnight"}
    description={d.unlocked ? "The birthday has passed, so every visitor sees the full experience immediately \u2014 letter, gallery, video, song, gifts." : "Visitors see the teaser and the live countdown. Protected content cannot be fetched until the moment below."}
    aside={<button type="button" onClick={doPublish} className="btn-gold" disabled={publish.isPending}>
          {publish.isPending ? "Publishing\u2026" : "Publish changes"}
        </button>}
  >
      {note && <div className="glass mb-5 rounded-xl border-[rgba(126,217,154,.35)] p-4 text-sm text-[#a8e6bd]">
          {note}
        </div>}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Stat
    label="State"
    value={d.unlocked ? "Unlocked" : "Locked"}
    pill={d.unlocked ? "live" : "locked"}
    sub={d.unlocked ? "Full experience is visible" : "Teaser + countdown only"}
  />
        <Stat
    label="Unlocks"
    value={formatInZone(birthday, d.timezone)}
    sub={`${d.timezone} \xB7 ${zoneLabel(birthday, d.timezone)}`}
  />
        <Stat
    label="Last published"
    value={d.publishedAt ? new Date(d.publishedAt).toLocaleString() : "Never"}
    sub={d.publishedBy ? `by ${d.publishedBy}` : "Press Publish when you're ready"}
  />

        <Stat label="Photos" value={String(d.mediaCounts.photo ?? 0)} sub="in the gallery" link="/admin/photos" />
        <Stat label="Videos" value={String(d.mediaCounts.video ?? 0)} sub="in the film section" link="/admin/videos" />
        <Stat label="Audio" value={String(d.mediaCounts.audio ?? 0)} sub="tracks for the player" link="/admin/music" />

        <Stat
    label="Messages"
    value={String(d.messageCount)}
    sub={`${d.scheduledCount} scheduled \xB7 ${d.hiddenCount} hidden/gift`}
    link="/admin/content"
  />
        <Stat
    label="Settings saved"
    value={new Date(d.updatedAt).toLocaleString()}
    sub="Every save writes to the database immediately"
    link="/admin/settings"
  />
        <Stat
    label="Animation"
    value={["Gentle", "Balanced", "Cinematic"][Math.max(0, Math.min(2, d.animationIntensity - 1))]}
    sub="Intensity of the 3D motion"
    link="/admin/settings"
  />
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div className="glass rounded-xl p-5">
          <p className="eyebrow">Preview as her</p>
          <p className="mt-2 text-sm text-[color:var(--muted-foreground)]">
            See exactly what she sees, without changing the real schedule.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to="/admin/schedule" className="btn-ghost-lux !min-h-[40px] !px-4 !text-[0.66rem]">
              Locked preview
            </Link>
            <Link to="/admin/schedule" className="btn-ghost-lux !min-h-[40px] !px-4 !text-[0.66rem]">
              Unlocked preview
            </Link>
          </div>
        </div>

        <div className="glass rounded-xl p-5">
          <p className="eyebrow">What she will see</p>
          <ul className="mt-3 space-y-1.5 text-sm text-[color:var(--muted-foreground)]">
            <li>
              <span className="text-[#e9a8f2]">Before midnight</span> — teaser, countdown, and only the media you
              marked as teaser-safe.
            </li>
            <li>
              <span className="text-[#e9a8f2]">At midnight</span> — the page switches over by itself, no refresh
              needed.
            </li>
            <li>
              <span className="text-[#e9a8f2]">Any time after</span> — the full experience, immediately on open.
            </li>
          </ul>
        </div>
      </div>
    </Panel>;
}
function Stat({
  label,
  value,
  sub,
  pill,
  link
}) {
  const inner = <div className="glass h-full rounded-xl p-5 transition hover:border-[rgba(233,168,242,.35)]">
      <div className="flex items-start justify-between gap-2">
        <p className="eyebrow">{label}</p>
        {pill && <span className={`pill ${pill === "live" ? "pill-live" : "pill-locked"}`}>{pill}</span>}
      </div>
      <p className="mt-2 font-display text-xl leading-tight text-[#f3f4f9]">{value}</p>
      {sub && <p className="mt-1.5 text-[0.72rem] leading-relaxed text-[color:var(--muted-foreground)]">{sub}</p>}
    </div>;
  return link ? <Link to={link}>{inner}</Link> : inner;
}
export {
  AdminOverview as default
};
